import {
  useInstallIntegration,
  usePatchIntegration
} from "@/api/useIntegrations"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"

import type {
  GcrInstallation,
  GcrInstallInput,
  GcrUpdateInput
} from "@/api/useIntegrations.types"
import type { ActionsIntegration } from "./useIntegration.types"

export interface GcrFormValues {
  locations?: string[]
  repositories?: string[]
  projectId?: string
}

type GcrAPIProps = Omit<ActionsIntegration, "onRemove"> & {
  registry?: GcrInstallation
}

const MESSAGE_PREFIX = "integrations.gcr.messages"

export const toGcrInstallInput = (values: GcrFormValues): GcrInstallInput => ({
  locations: values.locations || [],
  repositories: values.repositories || [],
  projectId: values.projectId?.trim().toLowerCase() || undefined
})

export const useGcrAPI = (props: GcrAPIProps) => {
  const { registry, t, message: messageApi } = props
  const vendor = INTEGRATION_VENDORS.gcr
  const formId = registry?.registryUrl || `${vendor}-form`
  const installer = useInstallIntegration({ vendor })
  const updater = usePatchIntegration({ vendor })

  const onFinish = async (values: GcrFormValues) => {
    const prefix = `${MESSAGE_PREFIX}.${registry ? "update" : "add"}`

    try {
      if (registry) {
        const gcr: GcrUpdateInput = {
          registryUrl: registry.registryUrl,
          repositories: values.repositories || []
        }

        await updater.mutateAsync({ data: { gcr } })
      } else {
        await installer.mutateAsync({
          data: { gcr: toGcrInstallInput(values) }
        })
      }
    } catch (error: any) {
      messageApi?.error(error?.data?.displayMessage || t(`${prefix}.failed`))

      return
    }

    messageApi?.success(t(`${prefix}.success`))

    if (registry) props.setIsDirty(false)
    else props.backToIntegration(true)
  }

  return {
    formId,
    onFinish,
    isPending: installer.isPending || updater.isPending
  }
}
